import type { WidgetConsentState } from '../../consent/consentState';

type GoogleAnalyticsConsentValue = 'granted' | 'denied';
type GoogleAnalyticsCommand = (...args: unknown[]) => void;
type GoogleAnalyticsDataLayer = IArguments[];

interface GoogleAnalyticsMeasurementNamespace {
  scriptId: string;
  dataLayerName: string;
  functionName: string;
}

interface GoogleAnalyticsMeasurementEntry {
  initialized: boolean;
  lastAppliedConsent: GoogleAnalyticsConsentValue;
  instanceConsentStates: Map<symbol, WidgetConsentState>;
}

interface GoogleAnalyticsConsentSettings {
  ad_storage: GoogleAnalyticsConsentValue;
  ad_user_data: GoogleAnalyticsConsentValue;
  ad_personalization: GoogleAnalyticsConsentValue;
  analytics_storage: GoogleAnalyticsConsentValue;
}

const GOOGLE_ANALYTICS_SCRIPT_ID_PREFIX = 'passenger-widget-gtag-script';
const GOOGLE_ANALYTICS_DATA_LAYER_PREFIX = 'passengerWidgetDataLayer';
const GOOGLE_ANALYTICS_FUNCTION_PREFIX = 'passengerWidgetGtag';

const googleAnalyticsMeasurements = new Map<
  string,
  GoogleAnalyticsMeasurementEntry
>();

function buildGoogleAnalyticsConsentSettings(
  consentValue: GoogleAnalyticsConsentValue,
): GoogleAnalyticsConsentSettings {
  return {
    ad_storage: consentValue,
    ad_user_data: consentValue,
    ad_personalization: consentValue,
    analytics_storage: consentValue,
  };
}

function resolveGoogleAnalyticsConsentValue(
  consentState: WidgetConsentState,
): GoogleAnalyticsConsentValue {
  if (consentState === 'granted') {
    return 'granted';
  }

  return 'denied';
}

function getGoogleAnalyticsMeasurementEntry(
  measurementId: string,
): GoogleAnalyticsMeasurementEntry {
  const existingEntry = googleAnalyticsMeasurements.get(measurementId);

  if (existingEntry) {
    return existingEntry;
  }

  const nextEntry: GoogleAnalyticsMeasurementEntry = {
    initialized: false,
    lastAppliedConsent: 'denied',
    instanceConsentStates: new Map(),
  };

  googleAnalyticsMeasurements.set(measurementId, nextEntry);

  return nextEntry;
}

function getWindowWithDynamicProperties() {
  return window as typeof window & Record<string, unknown>;
}

function getGoogleAnalyticsProperty<T>(propertyName: string): T | undefined {
  return getWindowWithDynamicProperties()[propertyName] as T | undefined;
}

function setGoogleAnalyticsProperty(
  propertyName: string,
  value: unknown,
): void {
  getWindowWithDynamicProperties()[propertyName] = value;
}

function deleteGoogleAnalyticsProperty(propertyName: string): void {
  delete getWindowWithDynamicProperties()[propertyName];
}

function ensureGoogleAnalyticsDataLayer(
  dataLayerName: string,
): GoogleAnalyticsDataLayer {
  const existingDataLayer =
    getGoogleAnalyticsProperty<GoogleAnalyticsDataLayer>(dataLayerName);

  if (existingDataLayer) {
    return existingDataLayer;
  }

  const nextDataLayer: GoogleAnalyticsDataLayer = [];

  setGoogleAnalyticsProperty(dataLayerName, nextDataLayer);

  return nextDataLayer;
}

function ensureGoogleAnalyticsCommand(
  dataLayerName: string,
  functionName: string,
): GoogleAnalyticsCommand {
  const existingCommand =
    getGoogleAnalyticsProperty<GoogleAnalyticsCommand>(functionName);

  if (existingCommand) {
    return existingCommand;
  }

  const dataLayer = ensureGoogleAnalyticsDataLayer(dataLayerName);

  const nextCommand: GoogleAnalyticsCommand = function gtag() {
    dataLayer.push(arguments);
  };

  setGoogleAnalyticsProperty(functionName, nextCommand);

  return nextCommand;
}

function ensureGoogleAnalyticsScript(measurementId: string): void {
  if (typeof document === 'undefined') {
    return;
  }

  const { scriptId, dataLayerName } =
    getGoogleAnalyticsMeasurementNamespace(measurementId);

  if (document.getElementById(scriptId)) {
    return;
  }

  const script = document.createElement('script');

  script.id = scriptId;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}&l=${encodeURIComponent(dataLayerName)}`;

  document.head.appendChild(script);
}

function resolveEffectiveConsentValue(
  consentStates: Iterable<WidgetConsentState>,
): GoogleAnalyticsConsentValue {
  let hasInstance = false;

  for (const consentState of consentStates) {
    hasInstance = true;

    if (resolveGoogleAnalyticsConsentValue(consentState) === 'denied') {
      return 'denied';
    }
  }

  if (!hasInstance) {
    return 'denied';
  }

  return 'granted';
}

function applyGoogleAnalyticsConsent(
  measurementId: string,
  consentValue: GoogleAnalyticsConsentValue,
  mode: 'default' | 'update',
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const { functionName } =
    getGoogleAnalyticsMeasurementNamespace(measurementId);
  const gtag = ensureGoogleAnalyticsCommand(
    getGoogleAnalyticsMeasurementNamespace(measurementId).dataLayerName,
    functionName,
  );

  gtag('consent', mode, buildGoogleAnalyticsConsentSettings(consentValue));
}

export function getGoogleAnalyticsMeasurementNamespace(
  measurementId: string,
): GoogleAnalyticsMeasurementNamespace {
  return {
    scriptId: `${GOOGLE_ANALYTICS_SCRIPT_ID_PREFIX}-${measurementId}`,
    // replace dashes with underscores to ensure the data layer name is a valid JavaScript identifier, since gtag.js will create a global variable with this name
    dataLayerName: `${GOOGLE_ANALYTICS_DATA_LAYER_PREFIX}_${measurementId.replace(/-/g, '_')}`,
    functionName: `${GOOGLE_ANALYTICS_FUNCTION_PREFIX}_${measurementId}`,
  };
}

export function initializeGoogleAnalyticsMeasurement(
  measurementId: string,
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const entry = getGoogleAnalyticsMeasurementEntry(measurementId);
  const { dataLayerName, functionName } =
    getGoogleAnalyticsMeasurementNamespace(measurementId);

  ensureGoogleAnalyticsDataLayer(dataLayerName);
  const gtag = ensureGoogleAnalyticsCommand(dataLayerName, functionName);

  ensureGoogleAnalyticsScript(measurementId);

  if (entry.initialized) {
    return;
  }

  applyGoogleAnalyticsConsent(measurementId, 'denied', 'default');
  gtag('js', new Date());
  gtag('config', measurementId, {
    send_page_view: false,
  });

  entry.initialized = true;
  entry.lastAppliedConsent = 'denied';
}

export function syncGoogleAnalyticsConsent(
  measurementId: string,
  instanceId: symbol,
  consentState: WidgetConsentState,
): void {
  if (typeof window === 'undefined') {
    return;
  }

  initializeGoogleAnalyticsMeasurement(measurementId);

  const entry = getGoogleAnalyticsMeasurementEntry(measurementId);

  entry.instanceConsentStates.set(instanceId, consentState);

  const nextConsentValue = resolveEffectiveConsentValue(
    entry.instanceConsentStates.values(),
  );

  if (nextConsentValue === entry.lastAppliedConsent) {
    return;
  }

  applyGoogleAnalyticsConsent(measurementId, nextConsentValue, 'update');
  entry.lastAppliedConsent = nextConsentValue;
}

export function clearGoogleAnalyticsConsent(
  measurementId: string,
  instanceId: symbol,
): void {
  const entry = googleAnalyticsMeasurements.get(measurementId);

  if (!entry) {
    return;
  }

  entry.instanceConsentStates.delete(instanceId);

  const nextConsentValue = resolveEffectiveConsentValue(
    entry.instanceConsentStates.values(),
  );

  if (nextConsentValue === entry.lastAppliedConsent) {
    return;
  }

  applyGoogleAnalyticsConsent(measurementId, nextConsentValue, 'update');
  entry.lastAppliedConsent = nextConsentValue;
}

export function sendGoogleAnalyticsEvent(
  measurementId: string,
  name: string,
  params: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const { functionName } =
    getGoogleAnalyticsMeasurementNamespace(measurementId);
  const gtag = getGoogleAnalyticsProperty<GoogleAnalyticsCommand>(functionName);

  if (!gtag) {
    return;
  }

  gtag('event', name, params);
}

export function resetGoogleAnalyticsForTesting(): void {
  if (typeof window !== 'undefined') {
    for (const measurementId of googleAnalyticsMeasurements.keys()) {
      const { dataLayerName, functionName } =
        getGoogleAnalyticsMeasurementNamespace(measurementId);

      deleteGoogleAnalyticsProperty(dataLayerName);
      deleteGoogleAnalyticsProperty(functionName);
    }
  }

  if (typeof document !== 'undefined') {
    for (const measurementId of googleAnalyticsMeasurements.keys()) {
      const { scriptId } =
        getGoogleAnalyticsMeasurementNamespace(measurementId);

      document.getElementById(scriptId)?.remove();
    }
  }

  googleAnalyticsMeasurements.clear();
}
